/**
 * Giphy Service
 * Handles fetching trending and search GIFs from Giphy API
 * with fallback curated reaction GIFs to ensure continuous availability.
 */

const FALLBACK_GIFS = [
  {
    id: "fb_1",
    title: "Awesome High Five",
    url: "https://media.giphy.com/media/l0MYt5jPR6QX5pnqM/giphy.gif",
    previewUrl: "https://media.giphy.com/media/l0MYt5jPR6QX5pnqM/200w.gif",
    tags: ["high five", "celebrate", "awesome", "yes", "team"]
  },
  {
    id: "fb_2",
    title: "Thumbs Up Leonardo",
    url: "https://media.giphy.com/media/XreQmk7ETCak0/giphy.gif",
    previewUrl: "https://media.giphy.com/media/XreQmk7ETCak0/200w.gif",
    tags: ["thumbs up", "great", "leonardo", "nod", "good"]
  },
  {
    id: "fb_3",
    title: "Mind Blown Galaxy",
    url: "https://media.giphy.com/media/26ufdipQqU2lhNA4g/giphy.gif",
    previewUrl: "https://media.giphy.com/media/26ufdipQqU2lhNA4g/200w.gif",
    tags: ["mind blown", "shocked", "wow", "galaxy", "universe"]
  },
  {
    id: "fb_4",
    title: "Clapping Standing Ovation",
    url: "https://media.giphy.com/media/7rj2ZgttvgomY/giphy.gif",
    previewUrl: "https://media.giphy.com/media/7rj2ZgttvgomY/200w.gif",
    tags: ["clapping", "applause", "bravo", "proud", "cheering"]
  },
  {
    id: "fb_5",
    title: "Excited Minion Yay",
    url: "https://media.giphy.com/media/11sBLVxNs7v6WA/giphy.gif",
    previewUrl: "https://media.giphy.com/media/11sBLVxNs7v6WA/200w.gif",
    tags: ["yay", "excited", "happy", "minion", "fun"]
  },
  {
    id: "fb_6",
    title: "Typing Fast Keyboard Hacker",
    url: "https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif",
    previewUrl: "https://media.giphy.com/media/JIX9t2j0ZTN9S/200w.gif",
    tags: ["cat", "typing", "work", "coding", "fast"]
  },
  {
    id: "fb_7",
    title: "Dancing Carlton",
    url: "https://media.giphy.com/media/pa37AAG5KXjhu/giphy.gif",
    previewUrl: "https://media.giphy.com/media/pa37AAG5KXjhu/200w.gif",
    tags: ["dance", "carlton", "celebration", "party", "happy"]
  },
  {
    id: "fb_8",
    title: "Success Baby Victory",
    url: "https://media.giphy.com/media/nXxOjZrbnbRxS/giphy.gif",
    previewUrl: "https://media.giphy.com/media/nXxOjZrbnbRxS/200w.gif",
    tags: ["success", "victory", "win", "baby", "yes"]
  },
  {
    id: "fb_9",
    title: "Laughing Ryan Gosling",
    url: "https://media.giphy.com/media/3oEjHAUOqG3lSS0f1C/giphy.gif",
    previewUrl: "https://media.giphy.com/media/3oEjHAUOqG3lSS0f1C/200w.gif",
    tags: ["laugh", "funny", "lol", "haha", "smile"]
  },
  {
    id: "fb_10",
    title: "Popcorn Watching Drama",
    url: "https://media.giphy.com/media/t3dLl0TGHCxTG/giphy.gif",
    previewUrl: "https://media.giphy.com/media/t3dLl0TGHCxTG/200w.gif",
    tags: ["popcorn", "waiting", "ready", "interested", "spectating"]
  }
];

const DEFAULT_GIPHY_KEY = "sXpGFDGZs0Dv1mmNFvYaGUvYwKX0PWIh";

export function getGiphyApiKey() {
  const localKey = localStorage.getItem("giphy_api_key");
  // Ignore legacy banned test key if stored locally
  if (localKey && localKey.trim() && localKey.trim() !== "dc6zaTOxFJmzC") {
    return localKey.trim();
  }

  const envKey = import.meta.env?.VITE_GIPHY_API_KEY;
  if (envKey && envKey.trim()) return envKey.trim();

  // Active verified Giphy SDK public demo key
  return DEFAULT_GIPHY_KEY;
}

export function setGiphyApiKey(key) {
  if (key && key.trim()) {
    localStorage.setItem("giphy_api_key", key.trim());
  } else {
    localStorage.removeItem("giphy_api_key");
  }
}

/**
 * Normalizes Giphy response item into our compact format
 */
function normalizeGiphyItem(item) {
  // Prefer downsized_medium / fixed_height for optimized chat animation bandwidth, fallback to original
  const original = 
    item.images?.downsized_medium?.url || 
    item.images?.fixed_height?.url || 
    item.images?.downsized?.url || 
    item.images?.original?.url ||
    item.images?.fixed_width?.url;

  const preview = 
    item.images?.fixed_width?.url || 
    item.images?.fixed_width_small?.url || 
    item.images?.downsized?.url || 
    original;

  return {
    id: item.id,
    title: item.title || "GIF",
    url: original,
    previewUrl: preview,
    width: item.images?.fixed_width?.width || 200,
    height: item.images?.fixed_width?.height || 200
  };
}

/**
 * Fetch trending GIFs from Giphy
 */
export async function fetchTrendingGifs(limit = 24) {
  const apiKey = getGiphyApiKey();
  try {
    const res = await fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=${limit}&rating=g`);
    if (!res.ok) {
      console.warn("Giphy API returned status:", res.status, "- using fallback collection");
      return FALLBACK_GIFS;
    }
    const json = await res.json();
    if (json.data && Array.isArray(json.data) && json.data.length > 0) {
      return json.data.map(normalizeGiphyItem);
    }
    return FALLBACK_GIFS;
  } catch (err) {
    console.warn("Failed to fetch trending GIFs from Giphy:", err);
    return FALLBACK_GIFS;
  }
}

/**
 * Search GIFs on Giphy
 */
export async function searchGifs(queryText, limit = 24) {
  if (!queryText || !queryText.trim()) {
    return fetchTrendingGifs(limit);
  }

  const apiKey = getGiphyApiKey();
  try {
    const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(queryText.trim())}&limit=${limit}&rating=g`);
    if (!res.ok) {
      console.warn("Giphy API search returned status:", res.status, "- using filtered fallback");
      return filterFallbackGifs(queryText);
    }
    const json = await res.json();
    if (json.data && Array.isArray(json.data) && json.data.length > 0) {
      return json.data.map(normalizeGiphyItem);
    }
    return filterFallbackGifs(queryText);
  } catch (err) {
    console.warn("Failed to search GIFs from Giphy:", err);
    return filterFallbackGifs(queryText);
  }
}

function filterFallbackGifs(queryText) {
  const q = queryText.toLowerCase().trim();
  const matched = FALLBACK_GIFS.filter(g => 
    g.title.toLowerCase().includes(q) || 
    (g.tags && g.tags.some(t => t.includes(q)))
  );
  return matched.length > 0 ? matched : FALLBACK_GIFS;
}
